/**
 * Estilos da aba Vendas assinadas e dos painéis de registrar, ligar e
 * cancelar. Mesmo desenho do resto da página: lista com divisórias (não
 * mural), texto neutro, UM sinal de cor por linha (o ponto de "mais de 30
 * dias") e nada de rolagem lateral: o meio encolhe com reticências.
 */
import styled from "styled-components";

import { BP_MOBILE } from "../../styles/canonical/breakpoints";

const tabletAcima = `(min-width: ${BP_MOBILE + 1}px)`;

export const ListaVendas = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
`;

/* Uma venda: [texto] [ações]; no celular as ações descem para baixo do texto. */
export const LinhaVenda = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 8px;
  min-width: 0;
  padding: 12px 14px;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.border};
  }

  .texto {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .cliente {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    font-size: 13.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.textPrimary};
  }

  .cliente > span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta,
  .sub {
    min-width: 0;
    font-size: var(--text-min, 12px);
    line-height: 1.45;
    color: ${({ theme }) => theme.textSecondary};
    overflow-wrap: anywhere;
  }

  .sub {
    color: ${({ theme }) => theme.textTertiary};
  }

  .sugestao {
    margin-top: 4px;
    font-size: var(--text-min, 12px);
    line-height: 1.45;
    color: ${({ theme }) => theme.textPrimary};
    overflow-wrap: anywhere;
  }

  .acoes {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  @media ${tabletAcima} {
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 16px;
    align-items: start;
    padding: 12px 18px;

    .acoes {
      justify-content: flex-end;
    }
  }
`;

/* Botão discreto das ações da linha (Ligar, Cancelar). */
export const BotaoLinha = styled.button`
  min-height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.surfaceAlt};
    }
  }
`;

/* Editor dos ambientes da venda: [nome] [classe] [remover]. */
export const ListaAmbientes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 110px) auto;
    gap: 6px;
    align-items: center;
    min-width: 0;
  }

  input,
  select {
    min-height: 36px;
    width: 100%;
    min-width: 0;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    /* 16px no celular: abaixo disso o iOS dá zoom ao focar. */
    font-size: 16px;
  }

  @media ${tabletAcima} {
    input,
    select {
      font-size: 13px;
    }
  }
`;

export const CampoTexto = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  font-size: var(--text-min, 12px);
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};

  textarea,
  input {
    width: 100%;
    max-width: 100%;
    min-height: 36px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    font-size: 16px;
    resize: vertical;
  }

  @media ${tabletAcima} {
    textarea,
    input {
      font-size: 13px;
    }
  }
`;

/* Seletor de pedido do Ligar: uma opção por linha, sugestão no topo. */
export const ListaPedidos = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  overflow: hidden;

  li + li {
    border-top: 1px solid ${({ theme }) => theme.border};
  }

  label {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 10px;
    align-items: start;
    padding: 9px 12px;
    min-width: 0;
    cursor: pointer;
  }

  label[data-bloqueado="sim"] {
    cursor: default;
  }

  input[type="radio"] {
    margin-top: 3px;
  }

  .nome {
    min-width: 0;
    font-size: 13px;
    font-weight: 550;
    color: ${({ theme }) => theme.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bloqueio {
    grid-column: 2;
    font-size: var(--text-min, 12px);
    line-height: 1.4;
    color: ${({ theme }) => theme.textSecondary};
    overflow-wrap: anywhere;
  }
`;

export const ErroInline = styled.p`
  margin: 0;
  font-size: var(--text-min, 12px);
  line-height: 1.45;
  color: var(--danger-text);
`;
